import React, { useState } from 'react';
import classNames from 'classnames';
import { useFormState } from 'react-final-form';
import { Button } from '@tcl-boron-prefabs/button';
import typography from '@tcl-boron-styles/typography/dist/index.module.css';
import {
  NEURO_CONSENT_TO_TEST_V2_KEY,
  NeuroConsentV2ContentPart1,
  NeuroConsentV2ContentPart2,
  NeuroConsentV2ContentPart3,
  NeuroConsentV2ContentPart4,
  neuroConsentV2RepresentativeFieldsBase,
  BillingType,
  OTHER_OPTION,
  TEXT_FIELD_CHARACTER_LIMIT,
  IsLegallyAuthorizedRepresentativeOption,
  IS_LEGALLY_AUTHORIZED_REPRESENTATIVE_OPTIONS_V2,
  extractDigits,
  allNeuroConsentV2Fields,
} from '@tempus/patient-forms-service-shared';
import { PortalViewsEnum } from '@tempus/portal-service-shared';
import { FinancialAssistanceStatuses, InsuranceType } from 'requisition-service-shared';
import { NavigationFormProps } from '../../../custom-to-do-list/components/NavigatorChecklist';
import { FormProps } from '../../forms';
import Form from '../../shared/Form';
import FormAlert, { AlertStatus } from '../../shared/FormAlert';
import FormBannerError from '../../shared/FormBannerError';
import FormTable, { FormTableFieldFrontend } from '../../shared/FormTable';
import Logo from '../../shared/Logo';
import { CHARACTER_LIMIT_WARNING, REQUIRED_FIELD_WARNING } from '../../shared/validation-utils';
import standaloneStyles from '../../shared/standalone-styles.module.css';
import { deleteUnmetConditionalFields, deleteFalsyStringFields } from '../../shared/utils';
import { PatientFormsTitleInjection } from '../../../custom-to-do-list/components/PatientFormsTitleInjection';
import BillingInformationForm, {
  CommercialInsurancePaymentPlanSubform,
  SelfPayPaymentPlanSubform,
} from './BillingInformationForm';
import PatientStandaloneFormAuthenticationManager, {
  PatientStandaloneFormAuthenticationValues,
  StandaloneFormAuthenticationError,
} from '../../shared/PatientStandaloneFormAuthenticationFlow';
import fetchHelper, { HttpError } from '../../../../utils';
import config from '../../../../../config';
import { AuthenticationType } from '../../../../api/AuthenticationType';
import InsuranceInformationSubform from './components/InsuranceInformationSubform';
import { combineFormConfigs } from '../../shared/utils';
import { NeuroConsentV2StartFormResponse } from '../../../../types/NeuroConsentV2';
import { ErrorPalette, SuccessPalette } from '@tcl-boron-colors/colors';
import { Fail, Success } from '@tcl-boron-icons/icons';
import CheckYourEmailAndPhone from '../../shared/CheckYourEmailAndPhone';
import NeuroStandaloneFormUnknownError from '../../shared/psychiatry/NeuroStandaloneFormUnknownError';
import Loader from '@tcl-boron-prefabs/loader';

export const NEURO_CONSENT_TO_TEST_V2_TITLE = 'Neurology Consent to Test';

const PAYMENT_DECLINED_STATUS = 402;

const NeuroConsentSubmitted: React.FC<{ referenceId?: string }> = ({ referenceId }) => (
  <div style={{ textAlign: 'center' }}>
    <div className={standaloneStyles.statusContainer}>
      <Success color={SuccessPalette[700]} />
      <p className={standaloneStyles.subtitle} style={{ margin: 0 }}>
        <strong>Consent Submitted</strong>
      </p>
    </div>
    <p className={standaloneStyles.subtitle}>Your Consent to Test has been successfully submitted. Thank you!</p>
    {referenceId && (
      <p className={standaloneStyles.subtitle}>
        Form Reference ID: <strong>{referenceId}</strong>
      </p>
    )}
  </div>
);

const NeuroConsentAlreadySubmitted: React.FC<{ referenceId?: string }> = ({ referenceId }) => (
  <div style={{ textAlign: 'center' }}>
    <div className={standaloneStyles.statusContainer}>
      <Success color={SuccessPalette[700]} />
      <p className={standaloneStyles.subtitle} style={{ margin: 0 }}>
        <strong>Consent Already Received</strong>
      </p>
    </div>
    <p className={standaloneStyles.subtitle}>
      We have already received your consent form. If you have any questions, please contact our support team at{' '}
      <a href="mailto:help@tempus.com">help@tempus.com</a>.
    </p>
    {referenceId && (
      <p className={standaloneStyles.subtitle}>
        Form Reference ID: <strong>{referenceId}</strong>
      </p>
    )}
  </div>
);

const NeuroConsentSubmissionError: React.FC<{ referenceId?: string; onRetry: () => void }> = ({
  referenceId,
  onRetry,
}) => (
  <div style={{ textAlign: 'center' }}>
    <div className={standaloneStyles.statusContainer}>
      <Fail color={ErrorPalette[700]} />
      <p className={standaloneStyles.subtitle} style={{ margin: 0 }}>
        <strong>Submission Error</strong>
      </p>
    </div>
    <p className={standaloneStyles.subtitle}>
      Something went wrong, please try again. If this issue persists, please contact the Tempus Customer Success team at{' '}
      <a href="mailto:help@tempus.com">help@tempus.com</a> or call <a href="tel:312-598-9961">312-598-9961</a>.
    </p>
    {referenceId && (
      <p className={standaloneStyles.subtitle}>
        Form Reference ID: <strong>{referenceId}</strong>
      </p>
    )}
    <div style={{ alignSelf: 'center', marginTop: '20px' }}>
      <Button ariaLabel="Retry" onClick={onRetry} data-testid="retry-button">
        Retry
      </Button>
    </div>
  </div>
);

const NeuroConsentCardDeclinedError: React.FC<{ referenceId?: string; onRetry: () => void }> = ({
  referenceId,
  onRetry,
}) => (
  <div style={{ textAlign: 'center' }}>
    <div className={standaloneStyles.statusContainer}>
      <Fail color={ErrorPalette[700]} />
      <p className={standaloneStyles.subtitle} style={{ margin: 0 }}>
        <strong>Payment Declined</strong>
      </p>
    </div>
    <p className={standaloneStyles.subtitle}>
      Your card was declined. Please try a different payment method. If this issue persists, please contact the Tempus
      Customer Success team at <a href="mailto:help@tempus.com">help@tempus.com</a> or call{' '}
      <a href="tel:312-598-9961">312-598-9961</a>.
    </p>
    {referenceId && (
      <p className={standaloneStyles.subtitle}>
        Form Reference ID: <strong>{referenceId}</strong>
      </p>
    )}
    <div style={{ alignSelf: 'center', marginTop: '20px' }}>
      <Button ariaLabel="Retry" onClick={onRetry} data-testid="card-declined-retry-button">
        Retry
      </Button>
    </div>
  </div>
);

const neuroConsentV2RepresentativeFieldsFrontend: FormTableFieldFrontend[] = [
  {
    name: 'isLegallyAuthorizedRepresentative',
    props: {
      placeholder: 'Select',
      dropdownOptions: IS_LEGALLY_AUTHORIZED_REPRESENTATIVE_OPTIONS_V2,
    },
    validate: (value: any | undefined): string | undefined => {
      if (!value) {
        return REQUIRED_FIELD_WARNING;
      }
    },
  },
  {
    conditionalValueConfig: {
      fieldNameToCheck: 'isLegallyAuthorizedRepresentative',
      valueToCheck: IsLegallyAuthorizedRepresentativeOption.YES,
      conditionalLogic: (value: any, conditionalValue: string) => {
        return value === conditionalValue;
      },
    },
    name: 'legallyAuthorizedRepresentativeFirstName',
    validate: (value: any | undefined): string | undefined => {
      if (!value) {
        return REQUIRED_FIELD_WARNING;
      } else if (String(value).length > TEXT_FIELD_CHARACTER_LIMIT) {
        return CHARACTER_LIMIT_WARNING;
      }
    },
  },
  {
    conditionalValueConfig: {
      fieldNameToCheck: 'isLegallyAuthorizedRepresentative',
      valueToCheck: IsLegallyAuthorizedRepresentativeOption.YES,
      conditionalLogic: (value: any, conditionalValue: string) => {
        return value === conditionalValue;
      },
    },
    name: 'legallyAuthorizedRepresentativeLastName',
    validate: (value: any | undefined): string | undefined => {
      if (!value) {
        return REQUIRED_FIELD_WARNING;
      } else if (String(value).length > TEXT_FIELD_CHARACTER_LIMIT) {
        return CHARACTER_LIMIT_WARNING;
      }
    },
  },
  {
    conditionalValueConfig: {
      fieldNameToCheck: 'isLegallyAuthorizedRepresentative',
      valueToCheck: IsLegallyAuthorizedRepresentativeOption.YES,
      conditionalLogic: (value: any, conditionalValue: string) => {
        return value === conditionalValue;
      },
    },
    name: 'legallyAuthorizedRepresentativeRelationshipToPatient',
    validate: (value: any | undefined): string | undefined => {
      if (!value) {
        return REQUIRED_FIELD_WARNING;
      } else if (String(value).length > TEXT_FIELD_CHARACTER_LIMIT) {
        return CHARACTER_LIMIT_WARNING;
      }
    },
  },
  {
    conditionalValueConfig: {
      fieldNameToCheck: 'isLegallyAuthorizedRepresentative',
      valueToCheck: IsLegallyAuthorizedRepresentativeOption.YES,
      conditionalLogic: (value: any, conditionalValue: string) => {
        return value === conditionalValue;
      },
    },
    name: 'representativeSignature',
    props: { placeholder: 'Sign here' },
    validate: (value: any | undefined) => {
      if (!value) return REQUIRED_FIELD_WARNING;
    },
  },
  {
    conditionalValueConfig: {
      fieldNameToCheck: 'isLegallyAuthorizedRepresentative',
      valueToCheck: IsLegallyAuthorizedRepresentativeOption.NO,
      conditionalLogic: (value: any, conditionalValue: string) => {
        return value === conditionalValue;
      },
    },
    name: 'signature',
    props: { placeholder: 'Sign here' },
    validate: (value: any | undefined) => {
      if (!value) return REQUIRED_FIELD_WARNING;
    },
  },
];

const neuroConsentV2SignatureFields = combineFormConfigs(
  neuroConsentV2RepresentativeFieldsFrontend,
  neuroConsentV2RepresentativeFieldsBase,
);

type NeuroConsentV2StartFormProps = PatientStandaloneFormAuthenticationValues & NeuroConsentV2StartFormResponse;

type NeuroConsentV2Props = FormProps & {
  startFormProps: NeuroConsentV2StartFormProps;
  setIsStripeComplete: (value: boolean) => void;
  isStripeComplete: boolean;
};

const NeuroConsentV2: React.FC<NeuroConsentV2Props> = (props) => {
  const formState = useFormState();
  const currentBillingType = formState.values['billingType'] as BillingType;
  const insuranceCompanyName = formState.values['insuranceCompanyName'];

  const requireBillTypeSelection = props.startFormProps.requireBillTypeSelection;
  const faStatus = props.startFormProps.faStatus;
  const stripeCheckoutToken = props.startFormProps.stripeCheckoutToken;
  const shouldCollectGovernmentInsurance = props.startFormProps.collectGovernmentInsurance;
  const useSelfPayFlow = insuranceCompanyName === OTHER_OPTION.value;

  const shouldPay =
    faStatus !== FinancialAssistanceStatuses.ApprovedForFullCoverage &&
    [BillingType.SELF_PAY, BillingType.COMMERCIAL_INSURANCE].includes(currentBillingType);
  const submitText = shouldPay ? 'Submit and Pay' : 'Submit';

  const bypassStripeRequirement = ![BillingType.SELF_PAY, BillingType.COMMERCIAL_INSURANCE].includes(
    currentBillingType,
  );

  return (
    <>
      {formState.submitFailed ? <FormBannerError /> : undefined}
      <NeuroConsentV2ContentPart1 />
      {requireBillTypeSelection && <BillingInformationForm />}
      <NeuroConsentV2ContentPart2
        faStatus={faStatus}
        billingType={currentBillingType}
        useSelfPayFlow={useSelfPayFlow}
        onClickFaLink={() => {
          window.location.pathname = '/form/neurology/financial-assistance';
        }}
      />
      {currentBillingType === BillingType.PUBLIC_INSURANCE && shouldCollectGovernmentInsurance && (
        <InsuranceInformationSubform insuranceType={InsuranceType.Public} />
      )}
      {currentBillingType === BillingType.COMMERCIAL_INSURANCE && (
        <>
          <InsuranceInformationSubform insuranceType={InsuranceType.Private} />
          {insuranceCompanyName && (
            <CommercialInsurancePaymentPlanSubform
              faStatus={faStatus}
              stripeCheckoutToken={stripeCheckoutToken}
              setStripeComplete={props.setIsStripeComplete}
              shouldCollectGovernmentInsurance={shouldCollectGovernmentInsurance}
            />
          )}
        </>
      )}
      {currentBillingType === BillingType.SELF_PAY && (
        <SelfPayPaymentPlanSubform
          faStatus={faStatus}
          stripeCheckoutToken={stripeCheckoutToken}
          setStripeComplete={props.setIsStripeComplete}
          shouldCollectGovernmentInsurance={shouldCollectGovernmentInsurance}
        />
      )}
      <NeuroConsentV2ContentPart3 />
      <NeuroConsentV2ContentPart4 />
      <div className={standaloneStyles.formInputContainer}>
        <FormTable fields={neuroConsentV2SignatureFields} />
      </div>
      <div className={standaloneStyles.footer}>
        {props.showAlert && (
          <FormAlert
            setShowAlert={props.setShowAlert}
            message={''}
            title={'Please fill out your payment information before submitting the form.'}
            status={AlertStatus.WARNING}
          />
        )}
        <Button
          loading={formState.submitting}
          ariaLabel="neuro-consent-to-test-v2-submit"
          type="submit"
          disabled={formState.invalid || (!bypassStripeRequirement && !props.isStripeComplete) || formState.submitting}
        >
          {submitText}
        </Button>
      </div>
    </>
  );
};

const NeuroConsentToTestV2Wrapper: React.FunctionComponent<NavigationFormProps> = (props) => {
  const [isStripeComplete, setIsStripeComplete] = useState(false);
  const [showStripeAlert, setShowStripeAlert] = useState<boolean>(false);
  const [startFormProps, setStartFormProps] = useState<NeuroConsentV2StartFormProps | null>(null);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [isErrorStartingForm, setIsErrorStartingForm] = useState<boolean>(false);
  const [isSubmissionError, setIsSubmissionError] = useState<boolean>(false);
  const [isCardDeclined, setIsCardDeclined] = useState<boolean>(false);
  const [isSendTokenLinkError, setIsSendTokenLinkError] = useState<boolean>(false);
  const [isLinkSent, setIsLinkSent] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const onSuccessfulAuthentication = async (patientLookupValues: PatientStandaloneFormAuthenticationValues) => {
    const {
      patientId,
      patientFirstName,
      patientLastName,
      patientDateOfBirth,
      patientEmail,
      patientPhoneNumber,
    } = patientLookupValues;
    try {
      setIsLoading(true);
      const data = await fetchHelper<any>(`/form/standalone/start`, {
        baseUrl: config.patientWebServiceBaseUrl,
        method: 'POST',
        headerOverrides: {
          'x-selected-portal-view': PortalViewsEnum.PSYCHIATRY,
        },
        body: {
          formType: NEURO_CONSENT_TO_TEST_V2_KEY,
          patientId,
          patientFirstName,
          patientLastName,
          patientDateOfBirth,
          patientEmail,
          patientPhoneNumber: extractDigits(patientPhoneNumber),
        },
        authenticationType: AuthenticationType.MANUAL,
        authToken: patientLookupValues.identityVerificationToken,
      });

      setStartFormProps({
        ...patientLookupValues,
        ...data,
      });
    } catch (error) {
      setIsErrorStartingForm(true);
      setStartFormProps(null);
      return;
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (values: any) => {
    deleteUnmetConditionalFields(values, allNeuroConsentV2Fields);
    deleteFalsyStringFields(values);

    const postBody = {
      formType: NEURO_CONSENT_TO_TEST_V2_KEY,
      data: {
        patientFirstName: startFormProps?.patientFirstName,
        patientLastName: startFormProps?.patientLastName,
        patientDateOfBirth: startFormProps?.patientDateOfBirth,
        patientEmail: startFormProps?.patientEmail,
        patientPhoneNumber: startFormProps?.patientPhoneNumber,
        stripeCustomerId: startFormProps?.stripeCustomerId,
        collectGovernmentInsurance: startFormProps?.collectGovernmentInsurance,
        useSelfPayFlow: values.insuranceCompanyName === OTHER_OPTION.value,
        faStatus: startFormProps?.faStatus,
        ...values,
      },
      patientId: startFormProps?.patientId,
    };

    const bypassStripeRequirement = ![BillingType.SELF_PAY, BillingType.COMMERCIAL_INSURANCE].includes(
      values.billingType,
    );

    if (!bypassStripeRequirement && !isStripeComplete) {
      setShowStripeAlert(true);
    } else {
      setShowStripeAlert(false);

      try {
        await fetchHelper<any>(`/form/standalone/submit`, {
          baseUrl: config.patientWebServiceBaseUrl,
          method: 'POST',
          body: postBody,
          authenticationType: AuthenticationType.MANUAL,
          authToken: startFormProps?.identityVerificationToken,
          headerOverrides: {
            'x-selected-portal-view': PortalViewsEnum.PSYCHIATRY,
          },
        });
        setIsSubmitted(true);
      } catch (error) {
        if (error instanceof HttpError && error.status === PAYMENT_DECLINED_STATUS) {
          setIsCardDeclined(true);
        } else {
          setIsSubmissionError(true);
        }
      }
    }
  };

  const showFormState = startFormProps && !startFormProps.patientConsentReceived;
  const isAlreadySubmitted = startFormProps && startFormProps.patientConsentReceived;

  const clearForm = () => {
    setStartFormProps(null);
    setIsLinkSent(false);
    setIsErrorStartingForm(false);
    setIsSendTokenLinkError(false);
    setIsSubmissionError(false);
    setIsCardDeclined(false);
  };

  const onRetry = async () => {
    setIsSubmissionError(false);
    setIsSubmitted(false);
    setIsStripeComplete(false);
    setIsCardDeclined(false);
    await onSuccessfulAuthentication(startFormProps!);
  };

  return (
    <PatientFormsTitleInjection>
      <div className={classNames(typography.body, standaloneStyles.screen)}>
        <div className={standaloneStyles.header}>
          <div className={standaloneStyles.headerLogo}>
            <Logo />
          </div>
        </div>
        <div className={standaloneStyles.content}>
          <div className={standaloneStyles.titleContainer}>
            <p className={standaloneStyles.title}>{NEURO_CONSENT_TO_TEST_V2_TITLE}</p>
            {startFormProps?.referenceId && (
              <p className={standaloneStyles.subtitle}>
                Form Reference ID: <strong>{startFormProps.referenceId}</strong>
              </p>
            )}
          </div>
          {isLinkSent && <CheckYourEmailAndPhone variant="consent" />}
          <PatientStandaloneFormAuthenticationManager
            onAuthenticationSuccess={onSuccessfulAuthentication}
            onAuthenticationError={() => {
              clearForm();
              setIsErrorStartingForm(true);
            }}
            onClear={clearForm}
            onSendTokenizedLinkDispatched={() => {
              clearForm();
              setIsLinkSent(true);
            }}
            onSendTokenizedLinkError={() => {
              clearForm();
              setIsSendTokenLinkError(true);
            }}
            formType={NEURO_CONSENT_TO_TEST_V2_KEY}
          />
          {isSendTokenLinkError ? (
            <NeuroStandaloneFormUnknownError referenceId={startFormProps?.referenceId} />
          ) : isErrorStartingForm ? (
            <StandaloneFormAuthenticationError />
          ) : isSubmissionError ? (
            <NeuroConsentSubmissionError referenceId={startFormProps?.referenceId} onRetry={onRetry} />
          ) : isCardDeclined ? (
            <NeuroConsentCardDeclinedError referenceId={startFormProps?.referenceId} onRetry={onRetry} />
          ) : isSubmitted ? (
            <NeuroConsentSubmitted referenceId={startFormProps?.referenceId} />
          ) : isAlreadySubmitted ? (
            <NeuroConsentAlreadySubmitted referenceId={startFormProps?.referenceId} />
          ) : isLoading ? (
            <div className={standaloneStyles.loaderContainer}>
              <Loader />
            </div>
          ) : showFormState ? (
            <Form onSubmit={onSubmit}>
              <NeuroConsentV2
                showAlert={showStripeAlert}
                setShowAlert={setShowStripeAlert}
                startFormProps={startFormProps!}
                setIsStripeComplete={setIsStripeComplete}
                isStripeComplete={isStripeComplete}
                {...props}
              />
            </Form>
          ) : null}
        </div>
      </div>
    </PatientFormsTitleInjection>
  );
};

export default NeuroConsentToTestV2Wrapper;
